/**
 * LandingPage - Página de marketing principal do CampanhaFlow
 *
 * Design moderno com hero animado, glassmorphism, mock dashboard,
 * social proof, features ricas e pricing premium.
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  Bot,
  Check,
  CheckCheck,
  ChevronRight,
  ClipboardList,
  Clock,
  LayoutDashboard,
  LineChart,
  Map,
  Menu,
  MessageCircle,
  Palette,
  Rocket,
  Search,
  Send,
  Shield,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  UserPlus,
  Users,
  X,
  Zap,
} from 'lucide-react';
import { Logo } from '@/components/Logo';
import { usePageSEO } from '@/hooks/usePageSEO';

export function LandingPage() {
  usePageSEO({
    title: 'CampanhaFlow',
    description: 'A plataforma completa para sua campanha eleitoral: WhatsApp com IA, análise de votação por cidade, simulação de cenários, site da campanha e mensagens em massa — tudo em um único lugar.',
    ogImage: '/og-image.png',
  });

  return (
    <div className="bg-white text-ink-900 overflow-x-hidden">
      <Navbar />
      <main className="pt-16 md:pt-20">
        <Hero />
        <DemoHighlight />
        <ProductShowcase />
        <Features />
        <Stats />
        <Testimonials />
        <SetupSteps />
        <Pricing />
        <CTA />
      </main>
      <Footer />
    </div>
  );
}

/* ============================================================================
 * Navbar
 * ========================================================================= */

function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);

  const links = [
    { href: '/demo', label: 'Tour Interativo', isRoute: true },
    { href: '#features', label: 'Recursos' },
    { href: '#showcase', label: 'Plataforma' },
    { href: '#pricing', label: 'Planos' },
    { href: '#testimonials', label: 'Clientes' },
  ];

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className="absolute inset-0 bg-white/80 backdrop-blur-xl border-b border-ink-100" />
      <nav className="container-wide relative flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="group">
          <Logo withText size={36} />
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8 text-sm font-medium text-ink-600">
          {links.map((link) =>
            link.isRoute ? (
              <Link
                key={link.href}
                to={link.href}
                className="hover:text-primary-600 transition-colors font-semibold text-primary-600"
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.href}
                href={link.href}
                className="hover:text-ink-900 transition-colors"
              >
                {link.label}
              </a>
            )
          )}
        </div>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-2">
          <Link to="/login" className="btn-ghost text-sm">
            Entrar
          </Link>
          <Link to="/signup" className="btn-primary text-sm">
            Começar grátis
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Mobile menu button */}
        <button
          type="button"
          onClick={() => setMobileOpen((o) => !o)}
          className="md:hidden h-10 w-10 rounded-xl border border-ink-200 bg-white/80 backdrop-blur-md flex items-center justify-center text-ink-700 hover:border-primary-300 hover:text-primary-600 transition-colors"
          aria-label={mobileOpen ? 'Fechar menu' : 'Abrir menu'}
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      {/* Mobile menu drawer */}
      {mobileOpen && (
        <div className="md:hidden absolute inset-x-0 top-full bg-white/95 backdrop-blur-xl border-b border-ink-100 shadow-soft-lg animate-fade-in-down">
          <div className="container-wide py-6 flex flex-col gap-1">
            {links.map((link) =>
              link.isRoute ? (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="px-3 py-2.5 rounded-xl text-base font-semibold text-primary-600 hover:bg-primary-50 transition-colors"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="px-3 py-2.5 rounded-xl text-base font-medium text-ink-700 hover:bg-ink-50 hover:text-primary-600 transition-colors"
                >
                  {link.label}
                </a>
              )
            )}
            <div className="my-2 h-px bg-ink-100" />
            <Link
              to="/login"
              onClick={() => setMobileOpen(false)}
              className="px-3 py-2.5 rounded-xl text-base font-medium text-ink-700 hover:bg-ink-50"
            >
              Entrar
            </Link>
            <Link
              to="/signup"
              onClick={() => setMobileOpen(false)}
              className="btn-primary mt-2 w-full"
            >
              Começar grátis
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

/* ============================================================================
 * Hero
 * ========================================================================= */

function Hero() {
  return (
    <section className="relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 bg-mesh-1" />
      <div className="absolute inset-0 grid-pattern mask-fade-y opacity-50" />

      {/* Animated blobs */}
      <div className="blob bg-primary-300 w-56 md:w-72 h-56 md:h-72 -top-20 -left-20" />
      <div
        className="blob bg-accent-300 w-72 md:w-96 h-72 md:h-96 top-40 -right-20"
        style={{ animationDelay: '4s' }}
      />
      <div
        className="blob bg-primary-200 w-64 md:w-80 h-64 md:h-80 bottom-10 left-1/3"
        style={{ animationDelay: '8s' }}
      />

      <div className="container-wide relative py-10 md:py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-md border border-primary-200 shadow-soft mb-6 animate-fade-in-down">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500" />
            </span>
            <span className="text-[11px] md:text-xs font-semibold text-ink-700">
              Novo: análise eleitoral e simulação de cenários
            </span>
            <ChevronRight className="h-3 w-3 text-ink-500" />
          </div>

          <h1 className="font-display text-[2.25rem] sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-ink-900 leading-[1.05] tracking-tight text-balance animate-fade-in-up">
            Sua campanha eleitoral{' '}
            <span className="relative inline-block">
              <RotatingHeadline />
              <svg
                className="absolute -bottom-2 left-0 w-full h-3 text-accent-400"
                viewBox="0 0 200 12"
                fill="none"
              >
                <path
                  d="M2 9C2 9 50 1 100 4C150 7 198 3 198 3"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </h1>

          <p className="mt-6 text-base md:text-lg text-ink-600 max-w-2xl mx-auto leading-relaxed animate-fade-in-up delay-200 px-2">
            WhatsApp, eleitores, campanhas, análise de votação e site da campanha
            em um único lugar. Inteligência artificial atendendo 24 horas e sua
            equipe focando no que importa: conquistar votos.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 animate-fade-in-up delay-300">
            <Link to="/signup" className="btn-primary btn-lg group justify-center">
              Começar 7 dias grátis
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/demo" className="btn-secondary btn-lg justify-center group">
              Tour interativo
              <Sparkles className="h-4 w-4 text-accent-500 group-hover:text-accent-600 transition-colors" />
            </Link>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs md:text-sm text-ink-500 animate-fade-in delay-500">
            <span className="flex items-center gap-1.5">
              <Check className="h-4 w-4 text-emerald-500" />
              Sem cobrança nos 7 dias grátis
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="h-4 w-4 text-emerald-500" />
              Setup em 5 minutos
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="h-4 w-4 text-emerald-500" />
              Cancele quando quiser
            </span>
          </div>

          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-50 border border-accent-200 text-xs font-medium text-accent-700 animate-fade-in delay-700">
            <Sparkles className="h-3.5 w-3.5 text-accent-500" />
            <span>
              <strong className="font-bold">+25 modelos prontos</strong> de mensagem por ocasião
            </span>
          </div>
        </div>

        {/* Mock Dashboard - oculto no mobile, com 5 views ciclando no desktop */}
        <div className="hidden md:block relative mt-10 md:mt-16 max-w-5xl mx-auto animate-fade-in-up delay-500">
          <div className="absolute -inset-4 bg-gradient-to-r from-primary-500 to-accent-500 rounded-3xl blur-3xl opacity-20" />
          <MockDashboard />
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------------------
 * RotatingHeadline - Texto animado com copies de conversão
 * ------------------------------------------------------------------------- */

const HEADLINES = [
  'conversa melhor',
  'conquista mais votos',
  'engaja eleitores',
  'converte contatos',
  'vence eleições',
];

function RotatingHeadline() {
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState<'visible' | 'exiting' | 'entering'>('visible');

  useEffect(() => {
    const timer = setInterval(() => {
      setPhase('exiting');
      setTimeout(() => {
        setIndex((i) => (i + 1) % HEADLINES.length);
        setPhase('entering');
        setTimeout(() => setPhase('visible'), 50);
      }, 400);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  const animClass =
    phase === 'exiting'
      ? 'opacity-0 translate-y-4 blur-sm'
      : phase === 'entering'
        ? 'opacity-0 -translate-y-4 blur-sm'
        : 'opacity-100 translate-y-0 blur-0';

  return (
    <span
      className={`text-gradient inline-block transition-all duration-500 ease-out ${animClass}`}
    >
      {HEADLINES[index]}
    </span>
  );
}

/* ----------------------------------------------------------------------------
 * MockDashboard - Dashboard simulado com 5 views ciclando automaticamente
 * Views: Visao Geral, Contatos, Chat, Campanhas, IA
 * ------------------------------------------------------------------------- */

const VIEWS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'contacts', label: 'Contatos', icon: Users },
  { id: 'chat', label: 'Chat', icon: MessageCircle },
  { id: 'campaigns', label: 'Campanhas', icon: Send },
  { id: 'ai', label: 'IA', icon: Bot },
] as const;

type ViewId = (typeof VIEWS)[number]['id'];

function MockDashboard() {
  const [activeView, setActiveView] = useState<ViewId>('dashboard');
  const [paused, setPaused] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 767px)').matches
      : false
  );

  // Detecta resize entre mobile e desktop
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Auto-cicla a cada 3s no mobile, 5s no desktop
  useEffect(() => {
    if (paused) return;
    const intervalMs = isMobile ? 3000 : 5000;
    const interval = setInterval(() => {
      setActiveView((current) => {
        const idx = VIEWS.findIndex((v) => v.id === current);
        return VIEWS[(idx + 1) % VIEWS.length].id;
      });
    }, intervalMs);
    return () => clearInterval(interval);
  }, [paused, isMobile]);

  const activeViewData = VIEWS.find((v) => v.id === activeView)!;

  return (
    <div
      className="relative rounded-2xl bg-white border border-ink-200 shadow-2xl overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Browser chrome */}
      <div className="flex items-center gap-2 px-3 md:px-4 py-2.5 md:py-3 bg-ink-50 border-b border-ink-200">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-yellow-400" />
          <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-emerald-400" />
        </div>
        <div className="flex-1 flex justify-center">
          <div className="px-3 py-1 bg-white rounded-md text-[10px] md:text-xs text-ink-500 border border-ink-200 truncate max-w-[260px]">
            app.campanhaflow.com.br/{activeView === 'dashboard' ? '' : activeView}
          </div>
        </div>
      </div>

      {/* Dashboard content */}
      <div className="grid grid-cols-12 min-h-[380px] sm:min-h-[400px] md:min-h-[420px]">
        {/* Sidebar - desktop */}
        <div className="hidden md:flex col-span-2 flex-col gap-1 p-4 border-r border-ink-100 bg-ink-50/40">
          <div className="flex items-center gap-2 mb-6 px-2">
            <Logo size={26} />
            <span className="font-display font-bold text-xs">CampanhaFlow</span>
          </div>
          {VIEWS.map((view) => (
            <button
              key={view.id}
              type="button"
              onClick={() => setActiveView(view.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all text-left ${
                view.id === activeView
                  ? 'bg-gradient-to-r from-primary-600 to-accent-600 text-white shadow-soft'
                  : 'text-ink-600 hover:bg-white'
              }`}
            >
              <view.icon className="h-4 w-4 flex-shrink-0" />
              {view.label}
            </button>
          ))}
        </div>

        {/* Main */}
        <div className="col-span-12 md:col-span-10 p-4 md:p-6 relative overflow-hidden">
          {/* Mobile tabs - dots indicators */}
          <div className="md:hidden flex justify-center gap-1.5 mb-4">
            {VIEWS.map((view) => (
              <button
                key={view.id}
                type="button"
                onClick={() => setActiveView(view.id)}
                aria-label={view.label}
                className={`h-1.5 rounded-full transition-all ${
                  view.id === activeView
                    ? 'w-6 bg-gradient-to-r from-primary-500 to-accent-500'
                    : 'w-1.5 bg-ink-300'
                }`}
              />
            ))}
          </div>

          {/* View content with fade transition (key triggers re-mount) */}
          <div key={activeView} className="animate-fade-in">
            {activeView === 'dashboard' && <DashboardView />}
            {activeView === 'contacts' && <ContactsView />}
            {activeView === 'chat' && <ChatView />}
            {activeView === 'campaigns' && <CampaignsView />}
            {activeView === 'ai' && <AIView />}
          </div>

          {/* Active view label - mobile only */}
          <div className="md:hidden mt-3 text-center">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-ink-500 uppercase tracking-wider">
              <activeViewData.icon className="h-3 w-3" />
              {activeViewData.label}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * View 1: DashboardView - Visao geral com KPIs + grafico
 * ------------------------------------------------------------------------- */

function DashboardView() {
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-display text-base md:text-xl font-bold text-ink-900">
            Visão geral
          </div>
          <p className="text-[10px] md:text-xs text-ink-500">Atualizado há 2 minutos</p>
        </div>
        <div className="badge-success !text-[10px] !px-2 !py-0.5">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Ao vivo
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-4">
        <KpiCard label="Contatos" value="42.853" trend="+12%" color="primary" />
        <KpiCard label="Mensagens" value="128.4k" trend="+24%" color="accent" />
        <KpiCard label="Conversas" value="3.247" trend="+8%" color="emerald" />
        <KpiCard label="Engajamento" value="87%" trend="+5%" color="amber" />
      </div>

      <MessagesChart />
    </>
  );
}

/* ----------------------------------------------------------------------------
 * View 2: ContactsView - Lista de contatos com busca e filtros
 * ------------------------------------------------------------------------- */

function ContactsView() {
  const contacts = [
    { name: 'Maria da Silva', city: 'São Paulo', phone: '(11) 9****-1234', tag: 'Apoiadora', color: 'emerald' },
    { name: 'João Pereira', city: 'Campinas', phone: '(19) 9****-5678', tag: 'Voluntário', color: 'primary' },
    { name: 'Ana Costa', city: 'Santos', phone: '(13) 9****-9012', tag: 'Líder', color: 'accent' },
    { name: 'Carlos Mendes', city: 'Sorocaba', phone: '(15) 9****-3456', tag: 'Apoiador', color: 'emerald' },
  ];

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-display text-base md:text-xl font-bold text-ink-900">Contatos</div>
          <p className="text-[10px] md:text-xs text-ink-500">42.853 cadastrados</p>
        </div>
        <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-ink-100 text-[10px] md:text-xs text-ink-500 max-w-[120px] md:max-w-none">
          <Search className="h-3 w-3" />
          <span className="truncate">Buscar...</span>
        </div>
      </div>

      <div className="space-y-1.5 md:space-y-2">
        {contacts.map((c, i) => {
          const colorMap = {
            emerald: 'from-emerald-500 to-teal-500',
            primary: 'from-primary-500 to-blue-500',
            accent: 'from-accent-500 to-pink-500',
          } as const;
          const tagColor = {
            emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
            primary: 'bg-primary-50 text-primary-700 border-primary-200',
            accent: 'bg-accent-50 text-accent-700 border-accent-200',
          } as const;
          return (
            <div
              key={i}
              className="flex items-center gap-3 p-2 md:p-2.5 rounded-xl border border-ink-100 hover:border-primary-200 hover:bg-primary-50/30 transition-colors"
            >
              <div
                className={`h-8 w-8 md:h-9 md:w-9 rounded-full bg-gradient-to-br ${colorMap[c.color as keyof typeof colorMap]} flex items-center justify-center text-white font-bold text-xs flex-shrink-0`}
              >
                {c.name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs md:text-sm font-semibold text-ink-900 truncate">
                    {c.name}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-[10px] md:text-xs text-ink-500">
                  <span>{c.city}</span>
                  <span className="hidden sm:inline">·</span>
                  <span className="hidden sm:inline">{c.phone}</span>
                </div>
              </div>
              <span
                className={`px-2 py-0.5 rounded-full text-[9px] md:text-[10px] font-bold border ${tagColor[c.color as keyof typeof tagColor]}`}
              >
                {c.tag}
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ----------------------------------------------------------------------------
 * View 3: ChatView - Lista de conversas WhatsApp
 * ------------------------------------------------------------------------- */

function ChatView() {
  const conversations = [
    {
      name: 'Maria da Silva',
      preview: 'Obrigada! Vou repassar para os vizinhos 💙',
      time: '14:32',
      unread: 0,
      online: true,
    },
    {
      name: 'João Pereira',
      preview: 'Quando será o próximo ato de campanha?',
      time: '14:18',
      unread: 2,
      online: true,
    },
    {
      name: 'Ana Costa',
      preview: 'Vocês têm material gráfico para imprimir?',
      time: '13:55',
      unread: 1,
      online: false,
    },
    {
      name: 'Carlos Mendes',
      preview: 'Vou apoiar! Conta comigo no dia da eleição',
      time: '13:42',
      unread: 0,
      online: false,
    },
  ];

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-display text-base md:text-xl font-bold text-ink-900">Conversas</div>
          <p className="text-[10px] md:text-xs text-ink-500">3.247 ativas</p>
        </div>
        <div className="badge-success !text-[10px] !px-2 !py-0.5">
          <CheckCheck className="h-3 w-3" />
          98.7% entregues
        </div>
      </div>

      <div className="space-y-1.5">
        {conversations.map((conv, i) => (
          <div
            key={i}
            className="flex items-center gap-3 p-2 md:p-2.5 rounded-xl border border-ink-100 hover:bg-emerald-50/30 hover:border-emerald-200 transition-colors"
          >
            <div className="relative flex-shrink-0">
              <div className="h-8 w-8 md:h-9 md:w-9 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-bold text-xs">
                {conv.name.charAt(0)}
              </div>
              {conv.online && (
                <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 border-2 border-white" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs md:text-sm font-semibold text-ink-900 truncate">
                  {conv.name}
                </span>
                <span className="text-[9px] md:text-[10px] text-ink-500 flex-shrink-0">
                  {conv.time}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] md:text-xs text-ink-500 truncate">{conv.preview}</p>
                {conv.unread > 0 && (
                  <span className="flex-shrink-0 h-4 min-w-4 px-1 rounded-full bg-gradient-to-br from-primary-600 to-accent-600 text-white text-[9px] font-bold flex items-center justify-center">
                    {conv.unread}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/* ----------------------------------------------------------------------------
 * View 4: CampaignsView - Cards de campanhas com progresso
 * ------------------------------------------------------------------------- */

function CampaignsView() {
  const campaigns = [
    {
      title: 'Comício no Centro',
      status: 'Ativa',
      statusColor: 'emerald',
      sent: 8542,
      total: 12000,
      deliveryRate: 98.4,
      icon: Send,
    },
    {
      title: 'Convite para Reunião',
      status: 'Agendada',
      statusColor: 'amber',
      sent: 0,
      total: 5000,
      deliveryRate: 0,
      icon: Clock,
    },
    {
      title: 'Mensagem de Agradecimento',
      status: 'Concluída',
      statusColor: 'primary',
      sent: 24580,
      total: 24580,
      deliveryRate: 99.1,
      icon: CheckCheck,
    },
  ];

  const colorMap = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    primary: 'bg-primary-50 text-primary-700 border-primary-200',
  } as const;

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-display text-base md:text-xl font-bold text-ink-900">Campanhas</div>
          <p className="text-[10px] md:text-xs text-ink-500">3 ativas · 12 totais</p>
        </div>
        <div className="px-2.5 py-1 rounded-lg bg-gradient-to-r from-primary-600 to-accent-600 text-white text-[10px] md:text-xs font-bold flex items-center gap-1">
          <Send className="h-3 w-3" />
          Nova
        </div>
      </div>

      <div className="space-y-2 md:space-y-2.5">
        {campaigns.map((c, i) => {
          const progress = (c.sent / c.total) * 100;
          return (
            <div
              key={i}
              className="p-3 rounded-xl border border-ink-100 hover:border-primary-200 hover:bg-primary-50/20 transition-colors"
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center flex-shrink-0">
                    <c.icon className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
                  </div>
                  <span className="text-xs md:text-sm font-semibold text-ink-900 truncate">
                    {c.title}
                  </span>
                </div>
                <span
                  className={`px-2 py-0.5 rounded-full text-[9px] md:text-[10px] font-bold border ${colorMap[c.statusColor as keyof typeof colorMap]} flex-shrink-0`}
                >
                  {c.status}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded-full bg-ink-100 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary-500 to-accent-500 rounded-full transition-all duration-1000"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className="text-[10px] md:text-xs font-semibold text-ink-600 flex-shrink-0">
                  {c.sent.toLocaleString('pt-BR')} / {c.total.toLocaleString('pt-BR')}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ----------------------------------------------------------------------------
 * View 5: AIView - Chat animado com IA mostrando conversas reais
 * ------------------------------------------------------------------------- */

interface MockMessage {
  from: 'user' | 'bot';
  text: string;
  time: string;
}

const AI_CONVERSATIONS: { contact: string; initials: string; messages: MockMessage[] }[] = [
  {
    contact: 'Maria da Silva',
    initials: 'MS',
    messages: [
      { from: 'user', text: 'Oi! Quais são as propostas para a saúde?', time: '14:30' },
      { from: 'bot', text: 'Olá, Maria! Temos 3 pilares: ampliação de UBSs, mutirões de atendimento e telemedicina gratuita para todos.', time: '14:30' },
      { from: 'user', text: 'Que ótimo! Como funciona a telemedicina?', time: '14:31' },
      { from: 'bot', text: 'Você agenda pelo app e faz consulta por vídeo com médicos da rede pública. Sem custo e sem fila!', time: '14:31' },
      { from: 'user', text: 'Maravilha, vou divulgar nos grupos do bairro!', time: '14:32' },
      { from: 'bot', text: 'Obrigado pelo apoio, Maria! Se precisar de material digital para compartilhar, é só pedir aqui. 💙', time: '14:32' },
    ],
  },
  {
    contact: 'João Pereira',
    initials: 'JP',
    messages: [
      { from: 'user', text: 'E aí, quando é o próximo comício?', time: '15:10' },
      { from: 'bot', text: 'Fala, João! O próximo evento é sábado, dia 12, às 16h na Praça Central. Teremos música e propostas ao vivo!', time: '15:10' },
      { from: 'user', text: 'Posso levar meus amigos?', time: '15:11' },
      { from: 'bot', text: 'Claro! Quanto mais gente melhor. Posso enviar o convite digital pra você compartilhar?', time: '15:11' },
      { from: 'user', text: 'Manda sim, vou espalhar!', time: '15:12' },
      { from: 'bot', text: 'Enviado! Compartilhe pelo WhatsApp e redes sociais. Cada apoiador faz a diferença! 🎉', time: '15:12' },
    ],
  },
  {
    contact: 'Ana Costa',
    initials: 'AC',
    messages: [
      { from: 'user', text: 'Boa tarde! Vocês têm proposta para educação?', time: '16:45' },
      { from: 'bot', text: 'Boa tarde, Ana! Sim! Nosso plano inclui escola em tempo integral, tablets para alunos e reforma de 15 escolas.', time: '16:45' },
      { from: 'user', text: 'E creches? Meu bairro não tem nenhuma', time: '16:46' },
      { from: 'bot', text: 'Temos meta de 8 novas creches, priorizando bairros sem cobertura. Qual seu bairro? Posso verificar o cronograma.', time: '16:46' },
      { from: 'user', text: 'Jardim Esperança, zona leste', time: '16:47' },
      { from: 'bot', text: 'Jardim Esperança está na fase 1! Previsão de início em março. Vou te manter atualizada. 📚', time: '16:47' },
    ],
  },
];

function AIView() {
  const [convIdx, setConvIdx] = useState(0);
  const [visibleCount, setVisibleCount] = useState(0);

  const conv = AI_CONVERSATIONS[convIdx];
  const messages = conv.messages;

  // Revela mensagens uma a uma, depois cicla para próxima conversa
  useEffect(() => {
    setVisibleCount(0);
    let count = 0;
    const reveal = () => {
      count++;
      setVisibleCount(count);
      if (count < messages.length) {
        timer = setTimeout(reveal, 900 + Math.random() * 400);
      } else {
        // Após todas as mensagens, espera e troca de conversa
        timer = setTimeout(() => {
          setConvIdx((i) => (i + 1) % AI_CONVERSATIONS.length);
        }, 2500);
      }
    };
    let timer = setTimeout(reveal, 600);
    return () => clearTimeout(timer);
  }, [convIdx, messages.length]);

  // Auto-scroll para última mensagem (dentro do container, sem mover a página)
  const chatContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [visibleCount]);

  return (
    <>
      {/* Header - estilo chat real */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="relative">
            <div className="h-8 w-8 md:h-9 md:w-9 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-bold text-[10px] md:text-xs">
              {conv.initials}
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 border-2 border-white" />
          </div>
          <div>
            <div className="text-xs md:text-sm font-semibold text-ink-900">{conv.contact}</div>
            <div className="flex items-center gap-1 text-[9px] md:text-[10px] text-emerald-600">
              <Bot className="h-2.5 w-2.5" />
              <span>IA atendendo</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="text-[9px] md:text-[10px] text-ink-500 font-medium">
            {convIdx + 1}/{AI_CONVERSATIONS.length}
          </div>
          <div className="badge-success !text-[9px] !px-1.5 !py-0.5">
            <Sparkles className="h-2.5 w-2.5" />
            IA
          </div>
        </div>
      </div>

      {/* Messages area */}
      <div ref={chatContainerRef} className="h-[260px] sm:h-[270px] md:h-[280px] overflow-y-auto space-y-2 pr-1 scrollbar-thin">
        {messages.slice(0, visibleCount).map((msg, i) => {
          const isUser = msg.from === 'user';
          return (
            <div
              key={`${convIdx}-${i}`}
              className={`flex gap-1.5 animate-fade-in ${isUser ? 'justify-end' : ''}`}
            >
              {!isUser && (
                <div className="flex-shrink-0 h-5 w-5 md:h-6 md:w-6 rounded-full bg-gradient-to-br from-primary-600 to-accent-600 flex items-center justify-center mt-0.5">
                  <Bot className="h-2.5 w-2.5 md:h-3 md:w-3 text-white" />
                </div>
              )}
              <div className="max-w-[80%]">
                <div
                  className={`px-2.5 py-1.5 md:px-3 md:py-2 text-[10px] md:text-xs leading-relaxed ${
                    isUser
                      ? 'bg-gradient-to-br from-primary-600 to-accent-600 text-white rounded-2xl rounded-br-sm'
                      : 'bg-ink-100 text-ink-800 rounded-2xl rounded-bl-sm'
                  }`}
                >
                  {msg.text}
                </div>
                <div className={`flex items-center gap-1 mt-0.5 ${isUser ? 'justify-end' : ''}`}>
                  <span className="text-[8px] md:text-[9px] text-ink-500">{msg.time}</span>
                  {isUser && <CheckCheck className="h-2.5 w-2.5 text-primary-400" />}
                </div>
              </div>
            </div>
          );
        })}
        {/* Typing indicator */}
        {visibleCount < messages.length && (
          <div className="flex gap-1.5 animate-fade-in">
            <div className="flex-shrink-0 h-5 w-5 md:h-6 md:w-6 rounded-full bg-gradient-to-br from-primary-600 to-accent-600 flex items-center justify-center mt-0.5">
              <Bot className="h-2.5 w-2.5 md:h-3 md:w-3 text-white" />
            </div>
            <div className="bg-ink-100 rounded-2xl rounded-bl-sm px-3 py-2 flex items-center gap-1">
              <span className="h-1.5 w-1.5 bg-ink-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="h-1.5 w-1.5 bg-ink-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="h-1.5 w-1.5 bg-ink-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}
      </div>

      {/* Input bar */}
      <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-ink-100">
        <div className="flex-1 bg-ink-50 rounded-full px-3 py-1.5 text-[10px] md:text-xs text-ink-500 border border-ink-200">
          Digite sua mensagem...
        </div>
        <div className="h-7 w-7 rounded-full bg-gradient-to-br from-primary-600 to-accent-600 flex items-center justify-center">
          <Send className="h-3 w-3 text-white" />
        </div>
      </div>
    </>
  );
}

/* ----------------------------------------------------------------------------
 * MessagesChart - Gráfico SVG com curva suave (área + linha)
 * Simula dados realistas de mensagens enviadas nos últimos 14 dias
 * ------------------------------------------------------------------------- */

function MessagesChart() {
  // Dados simulados: ultimos 14 dias de mensagens (em milhares)
  // Padrão realista: cresce gradualmente com picos, queda no fim de semana
  const data = [
    { day: '23', value: 8.2 },
    { day: '24', value: 9.5 },
    { day: '25', value: 11.8 },
    { day: '26', value: 10.4 },
    { day: '27', value: 13.6 },
    { day: '28', value: 15.9 },
    { day: '29', value: 12.1 },
    { day: '30', value: 14.7 },
    { day: '31', value: 17.3 },
    { day: '01', value: 16.5 },
    { day: '02', value: 19.8 },
    { day: '03', value: 22.4 },
    { day: '04', value: 20.1 },
    { day: '05', value: 24.6 },
  ];

  // Dimensoes do viewBox
  const W = 700;
  const H = 180;
  const PADDING_X = 30;
  const PADDING_Y = 24;
  const innerW = W - PADDING_X * 2;
  const innerH = H - PADDING_Y * 2;

  const maxValue = Math.max(...data.map((d) => d.value));
  const minValue = 0;

  // Mapeia data points para coordenadas SVG
  const points = data.map((d, i) => ({
    x: PADDING_X + (i / (data.length - 1)) * innerW,
    y: PADDING_Y + innerH - ((d.value - minValue) / (maxValue - minValue)) * innerH,
    value: d.value,
    day: d.day,
  }));

  // Path suave com curvas Bezier cubicas (horizontal smoothing)
  const smoothPath = points
    .map((p, i, arr) => {
      if (i === 0) return `M ${p.x} ${p.y}`;
      const prev = arr[i - 1];
      const cpX = (prev.x + p.x) / 2;
      return `C ${cpX} ${prev.y} ${cpX} ${p.y} ${p.x} ${p.y}`;
    })
    .join(' ');

  // Path da area: mesma curva fechada ate a base
  const areaPath = `${smoothPath} L ${points[points.length - 1].x} ${PADDING_Y + innerH} L ${
    points[0].x
  } ${PADDING_Y + innerH} Z`;

  // Y axis labels (3 niveis)
  const yLabels = [maxValue, maxValue / 2, 0].map((v) => v.toFixed(0));

  // Highlight no ultimo ponto
  const lastPoint = points[points.length - 1];

  return (
    <div className="rounded-xl bg-gradient-to-br from-white to-primary-50/50 border border-primary-100 p-5">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-ink-900">Mensagens enviadas</span>
            <span className="badge bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px] px-2 py-0">
              <TrendingUp className="h-2.5 w-2.5" />
              +24%
            </span>
          </div>
          <p className="text-xs text-ink-500 mt-0.5">Últimos 14 dias · vs período anterior</p>
        </div>
        <div className="text-right">
          <div className="font-display text-2xl font-extrabold text-ink-900">128.412</div>
          <div className="text-[10px] text-ink-500 uppercase tracking-wider font-semibold">
            Total
          </div>
        </div>
      </div>

      {/* SVG Chart */}
      <div className="relative">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full h-auto"
          preserveAspectRatio="none"
          style={{ maxHeight: '180px' }}
        >
          <defs>
            <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#6366f1" stopOpacity="0.35" />
              <stop offset="50%" stopColor="#a855f7" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#6366f1" />
              <stop offset="100%" stopColor="#a855f7" />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="3" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Grid horizontal lines */}
          {[0, 0.5, 1].map((t, i) => {
            const y = PADDING_Y + innerH * t;
            return (
              <line
                key={i}
                x1={PADDING_X}
                y1={y}
                x2={W - PADDING_X}
                y2={y}
                stroke="#e2e8f0"
                strokeWidth="1"
                strokeDasharray="3,4"
              />
            );
          })}

          {/* Y axis labels */}
          {yLabels.map((label, i) => (
            <text
              key={i}
              x={PADDING_X - 8}
              y={PADDING_Y + (innerH * i) / 2 + 4}
              textAnchor="end"
              className="text-[9px] fill-ink-400 font-semibold"
            >
              {label}k
            </text>
          ))}

          {/* Area fill */}
          <path d={areaPath} fill="url(#areaGradient)" />

          {/* Smooth line */}
          <path
            d={smoothPath}
            fill="none"
            stroke="url(#lineGradient)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter="url(#glow)"
          />

          {/* Data points (small dots) */}
          {points.map((p, i) => (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r={i === points.length - 1 ? 5 : 2.5}
              fill="white"
              stroke="url(#lineGradient)"
              strokeWidth={i === points.length - 1 ? 3 : 2}
            />
          ))}

          {/* Tooltip do ultimo ponto */}
          <g>
            <rect
              x={lastPoint.x - 38}
              y={lastPoint.y - 38}
              width="76"
              height="26"
              rx="6"
              fill="#1e1b4b"
            />
            <text
              x={lastPoint.x}
              y={lastPoint.y - 26}
              textAnchor="middle"
              className="text-[9px] fill-white/70 font-medium"
            >
              Hoje
            </text>
            <text
              x={lastPoint.x}
              y={lastPoint.y - 16}
              textAnchor="middle"
              className="text-[11px] fill-white font-bold"
            >
              {lastPoint.value.toFixed(1)}k msgs
            </text>
            {/* Pointer triangle */}
            <path
              d={`M ${lastPoint.x - 4} ${lastPoint.y - 12} L ${lastPoint.x + 4} ${
                lastPoint.y - 12
              } L ${lastPoint.x} ${lastPoint.y - 8} Z`}
              fill="#1e1b4b"
            />
          </g>

          {/* X axis labels (a cada 2 dias) */}
          {points.map((p, i) =>
            i % 2 === 0 ? (
              <text
                key={i}
                x={p.x}
                y={H - 6}
                textAnchor="middle"
                className="text-[9px] fill-ink-400 font-medium"
              >
                {p.day}
              </text>
            ) : null
          )}
        </svg>
      </div>

      {/* Footer com mini metricas */}
      <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-ink-100">
        <MiniMetric label="Entregues" value="98.7%" color="emerald" />
        <MiniMetric label="Lidas" value="76.4%" color="primary" />
        <MiniMetric label="Respondidas" value="34.2%" color="accent" />
      </div>
    </div>
  );
}

function MiniMetric({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: 'emerald' | 'primary' | 'accent';
}) {
  const colors = {
    emerald: 'text-emerald-600',
    primary: 'text-primary-600',
    accent: 'text-accent-600',
  };
  return (
    <div className="text-center">
      <div className={`font-display text-base font-bold ${colors[color]}`}>{value}</div>
      <div className="text-[9px] text-ink-500 uppercase tracking-wider font-semibold mt-0.5">
        {label}
      </div>
    </div>
  );
}

function KpiCard({
  label,
  value,
  trend,
  color,
}: {
  label: string;
  value: string;
  trend: string;
  color: 'primary' | 'accent' | 'emerald' | 'amber';
}) {
  const colors = {
    primary: 'from-primary-50 to-white border-primary-100 text-primary-700',
    accent: 'from-accent-50 to-white border-accent-100 text-accent-700',
    emerald: 'from-emerald-50 to-white border-emerald-100 text-emerald-700',
    amber: 'from-amber-50 to-white border-amber-100 text-amber-700',
  };
  return (
    <div className={`rounded-xl bg-gradient-to-br ${colors[color]} border p-3`}>
      <div className="text-xs text-ink-500 mb-1">{label}</div>
      <div className="font-display text-lg font-bold text-ink-900">{value}</div>
      <div className="text-[10px] font-semibold mt-1">{trend}</div>
    </div>
  );
}

/* ============================================================================
 * Features
 * ========================================================================= */

function Features() {
  const features = [
    {
      icon: MessageCircle,
      title: 'WhatsApp com IA',
      description:
        'Atendimento 24 horas todos os dias. A inteligência artificial responde dúvidas dos eleitores na hora e passa a conversa para sua equipe sempre que precisa de um toque humano.',
      color: 'from-emerald-500 to-teal-600',
    },
    {
      icon: Send,
      title: 'Mensagens em Massa',
      description:
        'Envie mensagens personalizadas para milhares de eleitores ao mesmo tempo. Modelos prontos, agendamento e disparo no ritmo certo para o WhatsApp não bloquear seu número.',
      color: 'from-primary-500 to-accent-600',
    },
    {
      icon: Users,
      title: 'Base de Eleitores',
      description:
        'Importe sua lista de eleitores em minutos. Organize por cidade, bairro, perfil e histórico. Encontre qualquer apoiador na hora e saiba com quem falar primeiro.',
      color: 'from-blue-500 to-primary-600',
    },
    {
      icon: BarChart3,
      title: 'Análise Eleitoral',
      description:
        'Veja o histórico de votos de qualquer candidato em qualquer cidade do país. Descubra seus redutos fortes, onde precisa crescer e quem são seus principais adversários — tudo em mapas fáceis de ler.',
      color: 'from-cyan-500 to-blue-600',
    },
    {
      icon: Target,
      title: 'Simulação de Cenários',
      description:
        'Monte cenários da próxima eleição em 3 versões: otimista, realista e pessimista. Ajuste a transferência de votos cidade por cidade e veja, no mapa, onde você ganha e onde precisa trabalhar mais.',
      color: 'from-violet-500 to-purple-600',
    },
    {
      icon: Palette,
      title: 'Santinho Digital',
      description:
        'Crie seu site de campanha em minutos arrastando e soltando blocos prontos: propostas, biografia, conquistas, galeria, vídeo, contador eleitoral e formulário para captar eleitores. Modelos prontos e totalmente personalizáveis.',
      color: 'from-accent-500 to-pink-600',
    },
    {
      icon: ClipboardList,
      title: 'Pedidos do Eleitor',
      description:
        'Receba, organize e responda pedidos de moradores em um único lugar. Quadro visual de andamento, mapa de demandas por bairro, classificação automática por inteligência artificial e integração com o sistema da câmara.',
      color: 'from-indigo-500 to-purple-600',
    },
    {
      icon: LineChart,
      title: 'Tudo Sob Controle',
      description:
        'Acompanhe em tempo real quantas mensagens foram entregues, lidas e respondidas. Veja o engajamento da campanha sem precisar abrir planilha nenhuma.',
      color: 'from-amber-500 to-orange-600',
    },
    {
      icon: Shield,
      title: 'Seguro e Confidencial',
      description:
        'Seus dados protegidos com criptografia. Cada pessoa da equipe acessa só o que precisa, e tudo dentro da Lei Geral de Proteção de Dados (LGPD).',
      color: 'from-rose-500 to-red-600',
    },
  ];

  return (
    <section id="features" className="section relative">
      <div className="container-wide">
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <span className="badge-primary">
            <Zap className="h-3 w-3" />
            Recursos
          </span>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-ink-900 mt-4 text-balance">
            Tudo que sua campanha precisa em{' '}
            <span className="text-gradient-static">um único lugar</span>
          </h2>
          <p className="mt-4 text-lg text-ink-600">
            Da primeira conversa no WhatsApp até a análise de votação por cidade.
            Sem precisar contratar várias ferramentas separadas.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="group relative card-hover p-7 cursor-pointer"
            >
              <div
                className={`inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${feature.color} text-white shadow-soft mb-5 group-hover:scale-110 transition-transform`}
              >
                <feature.icon className="h-6 w-6" strokeWidth={2.5} />
              </div>
              <h3 className="font-display text-xl font-bold text-ink-900 mb-2">
                {feature.title}
              </h3>
              <p className="text-ink-600 text-sm leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================================
 * Product Showcase
 * ========================================================================= */

function DemoHighlight() {
  return (
    <section className="py-12 md:py-16 bg-gradient-to-b from-primary-50/40 to-white relative overflow-hidden">
      <div className="absolute inset-0 dot-pattern opacity-20 pointer-events-none" />
      <div className="container-narrow relative text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-primary-200 shadow-soft mb-5">
          <Sparkles className="h-4 w-4 text-accent-500" />
          <span className="text-xs font-semibold text-ink-700">Tour interativo</span>
        </div>

        <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-ink-900 mb-3">
          Veja o CampanhaFlow em <span className="text-gradient-static">ação</span>
        </h2>
        <p className="text-ink-500 text-base md:text-lg max-w-xl mx-auto mb-8">
          Veja passo a passo como sua campanha funciona em minutos.
          Tour interativo com tudo o que a plataforma oferece.
        </p>

        {/* Preview cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8 max-w-3xl mx-auto">
          {[
            { label: 'WhatsApp', icon: MessageCircle },
            { label: 'Eleitores', icon: Users },
            { label: 'Análise', icon: BarChart3 },
            { label: 'Cenários', icon: Map },
            { label: 'Santinho', icon: Palette },
            { label: 'Campanhas', icon: Send },
          ].map((item) => (
            <div key={item.label} className="glass-card !p-3 text-center">
              <item.icon className="h-5 w-5 text-primary-600 mx-auto mb-1.5" />
              <p className="text-[11px] font-semibold text-ink-700">{item.label}</p>
            </div>
          ))}
        </div>

        <Link
          to="/demo"
          className="btn-primary btn-lg group inline-flex shadow-glow"
        >
          Iniciar tour interativo
          <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
        </Link>

        <p className="text-xs text-ink-600 mt-3">
          Sem cadastro necessário · Leva menos de 5 minutos
        </p>
      </div>
    </section>
  );
}

/* ============================================================================
 * ProductShowcase
 * ========================================================================= */

function ProductShowcase() {
  return (
    <section id="showcase" className="section bg-gradient-to-b from-ink-50/40 to-white">
      <div className="container-wide">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <span className="badge-accent">
              <Bot className="h-3 w-3" />
              Inteligência Artificial
            </span>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-ink-900 mt-4 text-balance">
              IA que entende{' '}
              <span className="text-gradient-static">cada eleitor</span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-ink-600 leading-relaxed">
              Nossa inteligência artificial conversa como gente, responde dúvidas
              sobre suas propostas, agenda compromissos e chama sua equipe
              quando o eleitor precisa de uma atenção mais pessoal.
            </p>

            <ul className="mt-6 md:mt-8 space-y-3 md:space-y-4">
              {[
                'Aprende com suas propostas, biografia e plano de governo',
                'Entende o que o eleitor quer e como ele está se sentindo',
                'Chama sua equipe na hora certa, sem deixar o eleitor esperando',
                'Lembra de cada conversa anterior com o eleitor',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                    <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
                  </div>
                  <span className="text-ink-700 font-medium">{item}</span>
                </li>
              ))}
            </ul>

            <Link to="/signup" className="btn-primary btn-lg mt-10 inline-flex">
              Experimentar agora
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>

          <div className="relative">
            <div className="absolute -inset-8 bg-gradient-to-br from-primary-500/20 to-accent-500/20 rounded-full blur-3xl" />
            <ChatPreview />
          </div>
        </div>
      </div>
    </section>
  );
}

const SHOWCASE_CONVERSATIONS: { contact: string; initials: string; color: string; messages: MockMessage[] }[] = [
  {
    contact: 'Pedro Santos',
    initials: 'PS',
    color: 'from-blue-500 to-primary-600',
    messages: [
      { from: 'user', text: 'Oi! Vi a proposta de transporte e quero saber mais', time: '10:15' },
      { from: 'bot', text: 'Olá, Pedro! Nossa proposta inclui BRT em 3 novos corredores, integração com metrô e passe livre para estudantes. Qual tema te interessa mais?', time: '10:15' },
      { from: 'user', text: 'O passe livre! Meus filhos gastam muito com ônibus', time: '10:16' },
      { from: 'bot', text: 'Entendo! O passe livre vale para estudantes de rede pública e particular, do ensino fundamental ao superior. Sem limite de viagens! 🚌', time: '10:16' },
      { from: 'user', text: 'Incrível! Como faço pra ajudar na campanha?', time: '10:17' },
      { from: 'bot', text: 'Que bom contar com você! Vou te conectar com nosso coordenador de voluntários da sua região. Qual seu bairro?', time: '10:17' },
    ],
  },
  {
    contact: 'Lucia Ferreira',
    initials: 'LF',
    color: 'from-accent-500 to-pink-600',
    messages: [
      { from: 'user', text: 'Boa noite! Vi o material sobre segurança no bairro', time: '20:30' },
      { from: 'bot', text: 'Boa noite, Lucia! Sim, temos um plano completo: iluminação LED em 100% das ruas, câmeras inteligentes e ronda 24h. Quer detalhes?', time: '20:30' },
      { from: 'user', text: 'Sim! Minha rua é muito escura, já fui assaltada', time: '20:31' },
      { from: 'bot', text: 'Lamento ouvir isso, Lucia. A troca da iluminação é prioridade na fase 1. Posso registrar sua rua como demanda prioritária?', time: '20:31' },
      { from: 'user', text: 'Por favor! Rua das Palmeiras, 340', time: '20:32' },
      { from: 'bot', text: 'Registrado! Sua demanda foi encaminhada ao coordenador regional. Você receberá atualizações por aqui. Conte com a gente! 💪', time: '20:32' },
    ],
  },
  {
    contact: 'Roberto Lima',
    initials: 'RL',
    color: 'from-emerald-500 to-teal-600',
    messages: [
      { from: 'user', text: 'Quero saber sobre o programa de emprego jovem', time: '09:00' },
      { from: 'bot', text: 'Bom dia, Roberto! O programa conecta jovens de 16 a 24 anos com empresas parceiras. Inclui curso técnico + estágio remunerado de R$1.200/mês.', time: '09:00' },
      { from: 'user', text: 'Meu sobrinho tem 18 anos e tá sem emprego, ele pode?', time: '09:01' },
      { from: 'bot', text: 'Pode sim! Basta ter ensino médio completo ou estar cursando. As inscrições abrem no primeiro mês de governo. Deixo ele avisado?', time: '09:01' },
      { from: 'user', text: 'Deixa! O nome dele é Mateus Lima', time: '09:02' },
      { from: 'bot', text: 'Anotado! Mateus receberá uma notificação assim que as inscrições abrirem. Obrigado pelo interesse, Roberto! 🎯', time: '09:02' },
    ],
  },
];

function ChatPreview() {
  const [convIdx, setConvIdx] = useState(0);
  const [visibleCount, setVisibleCount] = useState(0);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const conv = SHOWCASE_CONVERSATIONS[convIdx];
  const messages = conv.messages;

  useEffect(() => {
    setVisibleCount(0);
    let count = 0;
    const reveal = () => {
      count++;
      setVisibleCount(count);
      if (count < messages.length) {
        timer = setTimeout(reveal, 1000 + Math.random() * 500);
      } else {
        timer = setTimeout(() => {
          setConvIdx((i) => (i + 1) % SHOWCASE_CONVERSATIONS.length);
        }, 3000);
      }
    };
    let timer = setTimeout(reveal, 800);
    return () => clearTimeout(timer);
  }, [convIdx, messages.length]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [visibleCount]);

  return (
    <div className="relative rounded-3xl bg-white border border-ink-200 shadow-2xl overflow-hidden max-w-md mx-auto">
      {/* Chat header */}
      <div className="flex items-center gap-3 px-5 py-3.5 border-b border-ink-100 bg-ink-50/40">
        <div className="relative">
          <div className={`h-10 w-10 rounded-full bg-gradient-to-br ${conv.color} flex items-center justify-center text-white font-bold text-sm`}>
            {conv.initials}
          </div>
          <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-500 border-2 border-white" />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-ink-900 text-sm">{conv.contact}</div>
          <div className="text-[10px] text-emerald-600 flex items-center gap-1">
            <Bot className="h-3 w-3" />
            IA atendendo automaticamente
          </div>
        </div>
        <div className="flex items-center gap-1">
          {SHOWCASE_CONVERSATIONS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === convIdx ? 'w-4 bg-gradient-to-r from-primary-500 to-accent-500' : 'w-1.5 bg-ink-300'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Messages */}
      <div ref={chatContainerRef} className="h-[340px] overflow-y-auto px-5 py-4 space-y-3 scrollbar-thin">
        {messages.slice(0, visibleCount).map((msg, i) => {
          const isUser = msg.from === 'user';
          return (
            <div
              key={`${convIdx}-${i}`}
              className={`flex gap-2 animate-fade-in ${isUser ? 'justify-end' : ''}`}
            >
              {!isUser && (
                <div className="flex-shrink-0 h-7 w-7 rounded-full bg-gradient-to-br from-primary-600 to-accent-600 flex items-center justify-center mt-0.5">
                  <Bot className="h-3.5 w-3.5 text-white" />
                </div>
              )}
              <div className="max-w-[78%]">
                <div
                  className={`px-3.5 py-2.5 text-[13px] leading-relaxed ${
                    isUser
                      ? 'bg-gradient-to-br from-primary-600 to-accent-600 text-white rounded-2xl rounded-br-sm'
                      : 'bg-ink-100 text-ink-800 rounded-2xl rounded-bl-sm'
                  }`}
                >
                  {msg.text}
                </div>
                <div className={`flex items-center gap-1 mt-0.5 ${isUser ? 'justify-end' : ''}`}>
                  <span className="text-[9px] text-ink-500">{msg.time}</span>
                  {isUser && <CheckCheck className="h-3 w-3 text-primary-400" />}
                </div>
              </div>
            </div>
          );
        })}
        {/* Typing indicator */}
        {visibleCount < messages.length && visibleCount > 0 && (
          <div className="flex gap-2 animate-fade-in">
            <div className="flex-shrink-0 h-7 w-7 rounded-full bg-gradient-to-br from-primary-600 to-accent-600 flex items-center justify-center mt-0.5">
              <Bot className="h-3.5 w-3.5 text-white" />
            </div>
            <div className="bg-ink-100 rounded-2xl rounded-bl-sm px-4 py-2.5 flex items-center gap-1.5">
              <span className="h-2 w-2 bg-ink-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="h-2 w-2 bg-ink-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="h-2 w-2 bg-ink-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}
      </div>

      {/* Input bar */}
      <div className="flex items-center gap-2 px-5 py-3 border-t border-ink-100 bg-white">
        <div className="flex-1 bg-ink-50 border border-ink-200 rounded-full px-4 py-2 text-sm text-ink-500">
          Digite sua mensagem...
        </div>
        <button
          type="button"
          aria-label="Enviar mensagem"
          className="h-9 w-9 rounded-full bg-gradient-to-br from-primary-600 to-accent-600 flex items-center justify-center text-white"
        >
          <Send className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/* ============================================================================
 * Stats
 * ========================================================================= */

function Stats() {
  const stats = [
    { value: '500+', label: 'Candidatos ativos' },
    { value: '12M+', label: 'Mensagens enviadas' },
    { value: '97%', label: 'Taxa de entrega' },
    { value: '24/7', label: 'IA disponível' },
  ];

  return (
    <section className="py-12 md:py-16">
      <div className="container-wide">
        <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-primary-700 via-primary-600 to-accent-700 p-8 md:p-8 lg:p-10">
          {/* Decoration */}
          <div className="absolute inset-0 grid-pattern opacity-20" />
          <div className="absolute top-0 right-0 w-96 h-96 bg-accent-500 rounded-full blur-3xl opacity-30" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-400 rounded-full blur-3xl opacity-30" />

          <div className="relative grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-4 text-white">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="font-display text-4xl sm:text-5xl md:text-5xl lg:text-6xl font-extrabold mb-1">
                  {stat.value}
                </div>
                <div className="text-primary-100 text-xs md:text-sm font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================================
 * Testimonials
 * ========================================================================= */

function Testimonials() {
  const testimonials = [
    {
      name: 'Maria Silva',
      role: 'Vereadora - SP',
      content:
        'O CampanhaFlow transformou nossa relação com os eleitores. A IA atende 70% das dúvidas e nossa equipe foca no que realmente importa.',
      avatar: 'MS',
    },
    {
      name: 'Carlos Mendes',
      role: 'Deputado Estadual - MG',
      content:
        'Conseguimos disparar campanhas para 50 mil contatos com taxa de entrega altíssima. O painel de analytics é fora de série.',
      avatar: 'CM',
    },
    {
      name: 'Ana Beatriz',
      role: 'Coordenadora de Campanha - RJ',
      content:
        'Setup em menos de 1 hora. Suporte prioritário sempre presente. Recomendo demais para qualquer candidato sério.',
      avatar: 'AB',
    },
  ];

  return (
    <section id="testimonials" className="section bg-ink-50/40">
      <div className="container-wide">
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <span className="badge-accent">
            <Star className="h-3 w-3" />
            Depoimentos
          </span>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-ink-900 mt-4 text-balance">
            Quem usa, recomenda
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {testimonials.map((t) => (
            <div key={t.name} className="card-hover p-7">
              <div className="flex gap-1 mb-5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-ink-700 leading-relaxed mb-6">"{t.content}"</p>
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-white font-bold text-sm">
                  {t.avatar}
                </div>
                <div>
                  <div className="font-semibold text-ink-900">{t.name}</div>
                  <div className="text-xs text-ink-500">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================================
 * SetupSteps — "Do cadastro ao primeiro disparo em 15 minutos"
 * ========================================================================= */

function SetupSteps() {
  const steps = [
    {
      number: '1',
      icon: UserPlus,
      title: 'Crie sua conta',
      description: 'Cadastre o cartão e tenha 7 dias grátis, sem cobrança.',
      time: '2 min',
    },
    {
      number: '2',
      icon: MessageCircle,
      title: 'Conecte o WhatsApp',
      description: 'Escaneie um QR Code e seu número fica conectado.',
      time: '1 min',
    },
    {
      number: '3',
      icon: Users,
      title: 'Importe seus eleitores',
      description: 'Suba sua lista em planilha ou cadastre na hora.',
      time: '5 min',
    },
    {
      number: '4',
      icon: Bot,
      title: 'Ative a inteligência artificial',
      description: 'Modelos prontos respondem dúvidas dos eleitores 24 horas.',
      time: '3 min',
    },
    {
      number: '5',
      icon: Send,
      title: 'Dispare sua primeira campanha',
      description: 'Escolha um modelo, segmente e envie. Pronto.',
      time: '4 min',
    },
  ];

  return (
    <section className="section bg-gradient-to-b from-white to-primary-50/30 relative overflow-hidden">
      <div className="absolute inset-0 dot-pattern opacity-20 pointer-events-none" />
      <div className="container-wide relative">
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <span className="badge-accent">
            <Rocket className="h-3 w-3" />
            Comece em minutos
          </span>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-ink-900 mt-4 text-balance">
            Do cadastro ao primeiro disparo em{' '}
            <span className="text-gradient-static">15 minutos</span>
          </h2>
          <p className="mt-4 text-lg text-ink-600">
            Um assistente te leva pela mão em 5 passos simples. Sem precisar
            chamar a equipe de tecnologia.
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {/* Linha conectora desktop */}
          <div className="hidden lg:block absolute top-12 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-primary-200 via-accent-200 to-primary-200" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
            {steps.map((step) => (
              <div
                key={step.number}
                className="relative bg-white rounded-2xl border border-ink-200 p-5 shadow-soft hover:shadow-soft-lg hover:-translate-y-1 transition-all duration-300"
              >
                <div className="relative flex items-center justify-center mb-4">
                  <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary-600 to-accent-600 flex items-center justify-center shadow-glow">
                    <step.icon className="h-7 w-7 text-white" strokeWidth={2.2} />
                  </div>
                  <div className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-white border-2 border-primary-500 flex items-center justify-center text-xs font-extrabold text-primary-700 shadow">
                    {step.number}
                  </div>
                </div>
                <div className="text-center">
                  <h3 className="font-display text-base font-bold text-ink-900 mb-1.5">
                    {step.title}
                  </h3>
                  <p className="text-xs text-ink-600 leading-relaxed mb-3">
                    {step.description}
                  </p>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] font-bold text-emerald-700">
                    <Clock className="h-2.5 w-2.5" />
                    {step.time}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link to="/signup" className="btn-primary btn-lg group inline-flex shadow-glow">
              Começar minha campanha agora
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <p className="text-xs text-ink-500 mt-3">
              7 dias grátis · Sem instalação · Cancele quando quiser
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================================
 * Pricing
 * ========================================================================= */

function Pricing() {
  const plans = [
    {
      name: 'Vereador',
      price: '297',
      description: 'Ideal para campanhas municipais',
      features: [
        '5.000 eleitores cadastrados',
        '10.000 mensagens por mês',
        '2 pessoas na equipe',
        '1 número de WhatsApp',
        'Site da campanha (santinho digital)',
        'Suporte por e-mail',
      ],
      featured: false,
    },
    {
      name: 'Deputado',
      price: '997',
      description: 'Para campanhas estaduais',
      features: [
        '50.000 eleitores cadastrados',
        '100.000 mensagens por mês',
        '10 pessoas na equipe',
        '3 números de WhatsApp',
        'Análise eleitoral e simulação de cenários',
        'Permissões por equipe',
        'Suporte prioritário',
      ],
      featured: true,
    },
    {
      name: 'Senador',
      price: '2.997',
      description: 'Para grandes operações',
      features: [
        'Eleitores ilimitados',
        '500.000 mensagens por mês',
        'Equipe ilimitada',
        '10 números de WhatsApp',
        'Análise eleitoral e simulação de cenários',
        'Atendimento dedicado com gerente de conta',
        'Suporte prioritário garantido',
      ],
      featured: false,
    },
  ];

  return (
    <section id="pricing" className="section">
      <div className="container-wide">
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <span className="badge-primary">
            <Rocket className="h-3 w-3" />
            Planos
          </span>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-ink-900 mt-4 text-balance">
            Preço justo para{' '}
            <span className="text-gradient-static">cada cargo</span>
          </h2>
          <p className="mt-4 text-lg text-ink-600">
            Escolha o plano ideal. Desconto progressivo em planos anuais.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 md:gap-6 max-w-6xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-3xl p-8 transition-all duration-300 ${
                plan.featured
                  ? 'bg-gradient-to-br from-primary-700 via-primary-600 to-accent-700 text-white shadow-glow-lg md:scale-105'
                  : 'bg-white border border-ink-200 text-ink-900 shadow-soft hover:shadow-soft-lg hover:-translate-y-1'
              }`}
            >
              {plan.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <div className="px-4 py-1 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-bold shadow-lg">
                    Mais escolhido
                  </div>
                </div>
              )}

              <div className="mb-6">
                <h3 className="font-display text-2xl font-bold mb-1">{plan.name}</h3>
                <p
                  className={`text-sm ${plan.featured ? 'text-primary-100' : 'text-ink-500'}`}
                >
                  {plan.description}
                </p>
              </div>

              <div className="mb-8">
                <div className="flex items-baseline gap-1">
                  <span className={`text-sm ${plan.featured ? 'text-primary-100' : 'text-ink-500'}`}>
                    R$
                  </span>
                  <span className="font-display text-4xl md:text-5xl font-extrabold">{plan.price}</span>
                  <span
                    className={`text-sm ${plan.featured ? 'text-primary-100' : 'text-ink-500'}`}
                  >
                    /mês
                  </span>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2.5 text-sm">
                    <Check
                      className={`h-5 w-5 flex-shrink-0 ${
                        plan.featured ? 'text-accent-200' : 'text-emerald-500'
                      }`}
                      strokeWidth={3}
                    />
                    <span className={plan.featured ? 'text-white' : 'text-ink-700'}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <Link
                to="/signup"
                className={`block text-center w-full py-3.5 rounded-2xl font-semibold text-sm transition-all ${
                  plan.featured
                    ? 'bg-white text-primary-700 hover:bg-primary-50 shadow-lg'
                    : 'bg-ink-900 text-white hover:bg-ink-800'
                }`}
              >
                Começar agora
              </Link>
            </div>
          ))}
        </div>

        <p className="text-center text-ink-500 mt-10 text-sm">
          Todos os planos incluem 7 dias grátis antes da primeira cobrança.
        </p>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-4 md:gap-6 text-xs text-ink-500">
          <span className="flex items-center gap-1.5">
            <Shield className="h-4 w-4 text-emerald-500" />
            Pagamento seguro pela Asaas
          </span>
          <span className="flex items-center gap-1.5">
            <Zap className="h-4 w-4 text-emerald-500" />
            Pix Automático, cartão ou boleto
          </span>
          <span className="flex items-center gap-1.5">
            <CheckCheck className="h-4 w-4 text-emerald-500" />
            Cancele com 1 clique
          </span>
        </div>
      </div>
    </section>
  );
}

/* ============================================================================
 * CTA
 * ========================================================================= */

function CTA() {
  return (
    <section className="section">
      <div className="container-wide">
        <div className="relative rounded-3xl overflow-hidden bg-ink-900 p-8 md:p-12 lg:p-20 text-center">
          <div className="absolute inset-0 bg-mesh-1 opacity-50" />
          <div className="absolute inset-0 grid-pattern opacity-10" />
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-500 rounded-full blur-3xl opacity-20" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent-500 rounded-full blur-3xl opacity-20" />

          <div className="relative max-w-3xl mx-auto">
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white text-balance">
              Pronto para ganhar{' '}
              <span className="text-gradient">a próxima eleição?</span>
            </h2>
            <p className="mt-6 text-lg text-ink-300 max-w-xl mx-auto">
              Entenda seus eleitores, simule cenários e converse com milhares de
              pessoas ao mesmo tempo. Tudo em uma única plataforma.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/signup" className="btn-primary btn-lg group">
                Começar 7 dias grátis
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="#features"
                className="text-ink-300 hover:text-white text-sm font-medium transition-colors"
              >
                Conhecer recursos
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================================
 * Footer
 * ========================================================================= */

function Footer() {
  return (
    <footer className="border-t border-ink-100 bg-ink-50/40 py-16">
      <div className="container-wide">
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          <div className="md:col-span-2">
            <Link to="/" className="inline-block mb-4">
              <Logo withText size={36} />
            </Link>
            <p className="text-ink-600 max-w-sm text-sm leading-relaxed">
              Tudo o que sua campanha precisa em um único lugar: WhatsApp com
              inteligência artificial, análise eleitoral, simulação de cenários
              e site da campanha.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-ink-900 mb-4 text-sm">Produto</h3>
            <ul className="space-y-3 text-sm text-ink-600">
              <li>
                <a href="#features" className="hover:text-ink-900 transition-colors">
                  Recursos
                </a>
              </li>
              <li>
                <a href="#pricing" className="hover:text-ink-900 transition-colors">
                  Planos
                </a>
              </li>
              <li>
                <Link to="/signup" className="hover:text-ink-900 transition-colors">
                  Começar grátis
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-ink-900 mb-4 text-sm">Empresa</h3>
            <ul className="space-y-3 text-sm text-ink-600">
              <li>
                <Link to="/terms" className="hover:text-ink-900 transition-colors">
                  Termos de uso
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-ink-900 transition-colors">
                  Privacidade
                </Link>
              </li>
              <li>
                <a href="mailto:contato@campanhaflow.com.br" className="hover:text-ink-900 transition-colors">
                  Contato
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-ink-200 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-ink-500">
            © 2026 CampanhaFlow. Todos os direitos reservados.
          </p>
          <p className="text-xs text-ink-600">
            Desenvolvido com ♥ para a democracia brasileira
          </p>
        </div>
      </div>
    </footer>
  );
}
